import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const TradingCalendar = ({ trades, habitLogs, tradingPlanRules, onDayClick, selectedDate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  // Get first day of month and total days
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDay = firstDayOfMonth.getDay(); // 0 = Sunday
  
  // Calculate days from previous month to show
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  const prevMonthDays = Array.from({ length: startingDay }, (_, i) => ({
    day: prevMonthLastDay - startingDay + i + 1,
    isCurrentMonth: false,
    isPrevMonth: true
  }));
  
  // Current month days
  const currentMonthDays = Array.from({ length: daysInMonth }, (_, i) => ({
    day: i + 1,
    isCurrentMonth: true,
    isPrevMonth: false
  }));
  
  // Calculate days from next month to show (to complete the grid)
  const totalCells = Math.ceil((startingDay + daysInMonth) / 7) * 7;
  const nextMonthDays = Array.from({ length: totalCells - startingDay - daysInMonth }, (_, i) => ({
    day: i + 1,
    isCurrentMonth: false,
    isPrevMonth: false
  }));
  
  const allDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  
  // Group trades by date, calculating P/L and points based on outcome
  const tradesByDate = {};
  trades.forEach(trade => {
    const dateKey = trade.date?.split('T')[0];
    if (dateKey) {
      if (!tradesByDate[dateKey]) {
        tradesByDate[dateKey] = { trades: [], totalPL: 0, totalPoints: null, hasPoints: false };
      }
      tradesByDate[dateKey].trades.push(trade);
      // Calculate P/L based on outcome (losses are negative)
      const pl = Math.abs(parseFloat(trade.profitLoss) || 0);
      if (trade.outcome?.toLowerCase() === 'loss') {
        tradesByDate[dateKey].totalPL -= pl;
      } else {
        tradesByDate[dateKey].totalPL += pl;
      }
      // Calculate points - only if pipsPoints was entered (not null/undefined/empty)
      const pipsPoints = trade.pipsPoints ?? trade.pips_points;
      if (pipsPoints !== null && pipsPoints !== undefined && pipsPoints !== '') {
        const pointsValue = Math.abs(parseFloat(pipsPoints) || 0);
        if (tradesByDate[dateKey].totalPoints === null) {
          tradesByDate[dateKey].totalPoints = 0;
        }
        tradesByDate[dateKey].hasPoints = true;
        if (trade.outcome?.toLowerCase() === 'loss') {
          tradesByDate[dateKey].totalPoints -= pointsValue;
        } else {
          tradesByDate[dateKey].totalPoints += pointsValue;
        }
      }
    }
  });
  
  // Group habit logs by date and RECALCULATE scores based on CURRENT rules only
  // This ensures deleted rules don't affect the displayed score
  const habitsByDate = useMemo(() => {
    const result = {};
    
    if (!tradingPlanRules || tradingPlanRules.length === 0) {
      // No current rules, so no habit tracking to display
      return result;
    }
    
    const currentRuleIds = new Set(tradingPlanRules.map(r => r.id));
    
    habitLogs.forEach(log => {
      if (!log.entries || log.entries.length === 0) {
        return;
      }
      
      // Filter entries to only include current rules
      const relevantEntries = log.entries.filter(entry => currentRuleIds.has(entry.rule_id));
      
      // If no entries match current rules, this day is "not tracked" for current rules
      if (relevantEntries.length === 0) {
        return;
      }
      
      // Check if at least one habit was actually followed
      // If all are unchecked (0%), don't show as tracked unless explicitly saved with current rules
      const followedCount = relevantEntries.filter(e => e.followed).length;
      
      // Recalculate score based on CURRENT rules only
      const recalculatedScore = (followedCount / tradingPlanRules.length) * 100;
      
      result[log.date] = {
        ...log,
        score: recalculatedScore,
        hasValidData: true // Has data that matches current rules
      };
    });
    
    return result;
  }, [habitLogs, tradingPlanRules]);
  
  // Calculate weekly totals
  const getWeeklyTotal = (weekStartIndex) => {
    let total = 0;
    let tradeCount = 0;
    for (let i = weekStartIndex; i < weekStartIndex + 7 && i < allDays.length; i++) {
      const dayInfo = allDays[i];
      if (dayInfo.isCurrentMonth) {
        const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayInfo.day).padStart(2, '0')}`;
        const dayData = tradesByDate[dateKey];
        if (dayData) {
          total += dayData.totalPL;
          tradeCount += dayData.trades.length;
        }
      }
    }
    return { total, tradeCount };
  };
  
  // Calculate monthly P/L
  let monthlyPL = 0;
  Object.entries(tradesByDate).forEach(([dateKey, data]) => {
    const [y, m] = dateKey.split('-').map(Number);
    if (y === year && m === month + 1) {
      monthlyPL += data.totalPL;
    }
  });
  
  const navigateMonth = (direction) => {
    setCurrentDate(new Date(year, month + direction, 1));
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  const isToday = (day, isCurrentMonth) => {
    const today = new Date();
    return isCurrentMonth && 
           day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  
  const handleDayClick = (dayInfo) => {
    if (!dayInfo.isCurrentMonth) return;
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayInfo.day).padStart(2, '0')}`;
    onDayClick(dateKey, tradesByDate[dateKey], habitsByDate[dateKey]);
  };
  
  // Split allDays into weeks
  const weeks = [];
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push(allDays.slice(i, i + 7));
  }
  
  return (
    <div className="trading-calendar">
      <div className="calendar-header">
        <div className="calendar-title">
          <span className="monthly-pl-label">Monthly P/L:</span>
          <span className={`monthly-pl-value ${monthlyPL >= 0 ? 'positive' : 'negative'}`}>
            {monthlyPL >= 0 ? '' : '-'}${Math.abs(monthlyPL).toFixed(2)}
          </span>
        </div>
      </div>
      
      <div className="calendar-nav">
        <div className="calendar-nav-left">
          <button className="calendar-nav-btn" onClick={() => navigateMonth(-1)}>
            <ChevronLeft size={18} />
          </button>
          <span className="calendar-month-year">{monthNames[month]} {year}</span>
          <button className="calendar-nav-btn" onClick={() => navigateMonth(1)}>
            <ChevronRight size={18} />
          </button>
        </div>
        <button className="calendar-today-btn" onClick={goToToday}>
          Today
        </button>
      </div>
      
      <div className="calendar-grid">
        {/* Day headers */}
        {dayNames.map((name, i) => (
          <div key={`header-${i}`} className="calendar-day-header">
            {name}
          </div>
        ))}
        
        {/* Calendar cells */}
        {weeks.map((week, weekIndex) => (
          <React.Fragment key={`week-${weekIndex}`}>
            {week.map((dayInfo, dayIndex) => {
              const dateKey = dayInfo.isCurrentMonth 
                ? `${year}-${String(month + 1).padStart(2, '0')}-${String(dayInfo.day).padStart(2, '0')}`
                : null;
              const dayData = dateKey ? tradesByDate[dateKey] : null;
              const hasHabitLog = dateKey ? habitsByDate[dateKey] : null;
              const isSaturday = dayIndex === 6;
              const weeklyData = isSaturday ? getWeeklyTotal(weekIndex * 7) : null;
              
              return (
                <div
                  key={`day-${weekIndex}-${dayIndex}`}
                  className={`calendar-day-cell ${!dayInfo.isCurrentMonth ? 'other-month' : ''} ${isToday(dayInfo.day, dayInfo.isCurrentMonth) ? 'today' : ''} ${dayData ? 'has-trades' : ''} ${selectedDate === dateKey ? 'selected' : ''}`}
                  onClick={() => handleDayClick(dayInfo)}
                >
                  <div className="day-number">{dayInfo.day}</div>
                  
                  {dayInfo.isCurrentMonth && dayData && (
                    <div className="day-stats">
                      <span className={`day-pl ${dayData.totalPL >= 0 ? 'positive' : 'negative'}`}>
                        {dayData.totalPL >= 0 ? '' : '-'}${Math.abs(dayData.totalPL).toFixed(2)}
                      </span>
                      <span className="day-trade-count">
                        {dayData.trades.length} trade{dayData.trades.length !== 1 ? 's' : ''}
                      </span>
                      {dayData.hasPoints && dayData.totalPoints !== null && (
                        <span className={`day-points ${dayData.totalPoints >= 0 ? 'positive' : 'negative'}`}>
                          {dayData.totalPoints >= 0 ? '+' : ''}{dayData.totalPoints % 1 === 0 ? dayData.totalPoints : dayData.totalPoints.toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Show habit score on the day - only if score > 0 (at least one habit followed) */}
                  {dayInfo.isCurrentMonth && hasHabitLog && hasHabitLog.score > 0 && (
                    <div className="day-habit-score">
                      {Number(hasHabitLog.score).toFixed(1)}%
                    </div>
                  )}
                  
                  {isSaturday && dayInfo.isCurrentMonth && (
                    <div className="weekly-summary">
                      <span className="week-label">Week {weekIndex + 1}</span>
                      <span className={`week-pl ${weeklyData.total >= 0 ? 'positive' : 'negative'}`}>
                        {weeklyData.total >= 0 ? '' : '-'}${Math.abs(weeklyData.total).toFixed(2)}
                      </span>
                      <span className="week-trade-count">
                        {weeklyData.tradeCount} trade{weeklyData.tradeCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default TradingCalendar;
